/**
 * ConditionalInterpretation — the "conditional decode" stage of the pipeline
 * (0704 §9, gap ⑮): the same low-level gaze event is interpreted DIFFERENTLY
 * by task context. A book glance is normal in an open-book test and worth a
 * neutral observation log in a closed-book test; a pen glance is always
 * normal; an uncertain gaze is suppressed rather than force-flagged.
 *
 * This is the highest-leverage FPR lever that needs no training data: most
 * naive false positives ("looked at a book!") come from context-free rules.
 * Conditioning on examMode + permitted materials suppresses them at the source.
 *
 * Output is NEVER a verdict — only a neutral observation label + the reason
 * and the context factor that drove the decision (explainability). Final
 * judgement is a human's.
 *
 * Pure logic. Sits on the seam: consumes a candidate derived from the
 * TargetRegistry / gaze-likelihood, emits an interpretation the policy layer
 * (scene rules) can gate on. Model-agnostic — unchanged when the deterministic
 * segmenter is later swapped for the HSMM.
 */

import type { SemanticRole, TargetType, TargetRegistry } from './TargetRegistry';
import type { GazeLikelihoodResult } from './GazeTargetLikelihood';

export type ExamMode =
  | 'closed_book'
  | 'open_book'
  | 'homework'
  | 'practice'
  | 'unspecified';

export interface TaskContext {
  examMode: ExamMode;
  /** Semantic roles permitted in this task (suppressed as normal). */
  permittedRoles?: SemanticRole[];
  /** Explicit permitted material labels/classes (finer grain than role). */
  permittedMaterials?: string[];
}

export interface GazeEventCandidate {
  targetType: TargetType | 'unknown';
  semanticRole: SemanticRole;
  /** Object label if a physical object (e.g. 'book', 'cell phone', 'pen'). */
  label?: string;
  /** Dwell duration (ms) of the gaze on this target. */
  durationMs: number;
  /** Probability the gaze is on this target (likelihood top.p). */
  targetProb?: number;
  /** Gaze distribution entropy (bits); high = diffuse/uncertain. */
  entropy?: number;
}

export type InterpretationAction =
  | 'suppress_as_normal'
  | 'observe'
  | 'accessibility_allowed';

export type InterpretationBasis =
  | 'own_sheet'
  | 'writing_tool'
  | 'permitted_material'
  | 'device_candidate'
  | 'other_person'
  | 'uncertain'
  | 'default';

export interface Interpretation {
  action: InterpretationAction;
  /** Neutral, observation-only label. Never a verdict. */
  neutralLabel: string;
  /** Why this decision was made (explainability). */
  reason: string;
  /** Which context factor drove it. */
  basis: InterpretationBasis;
}

export interface InterpretOpts {
  /** Entropy (bits) at/above which the gaze is treated as too uncertain to interpret. */
  entropyGate?: number;
  /** Minimum target probability below which the gaze is treated as uncertain. */
  minTargetProb?: number;
}

const OPEN_CONTEXTS: ReadonlySet<ExamMode> = new Set<ExamMode>([
  'open_book',
  'homework',
  'practice',
]);

function isPermitted(cand: GazeEventCandidate, ctx: TaskContext): boolean {
  if (ctx.permittedRoles?.includes(cand.semanticRole)) return true;
  if (cand.label && ctx.permittedMaterials?.some((m) => cand.label!.toLowerCase().includes(m.toLowerCase()))) {
    return true;
  }
  return false;
}

/**
 * Interpret one gaze-on-target event under a task context. Pure, deterministic.
 * The decision ladder is ordered so the cheapest/most-normal outcomes win first.
 */
export function interpretGazeEvent(
  cand: GazeEventCandidate,
  ctx: TaskContext,
  opts: InterpretOpts = {},
): Interpretation {
  const entropyGate = opts.entropyGate ?? 1.5;
  const minProb = opts.minTargetProb ?? 0.25;

  // 0) Uncertainty gate — do not interpret a diffuse/unknown gaze (FPR guard).
  if (
    cand.targetType === 'unknown' ||
    (cand.entropy !== undefined && cand.entropy >= entropyGate) ||
    (cand.targetProb !== undefined && cand.targetProb < minProb)
  ) {
    return {
      action: 'suppress_as_normal',
      neutralLabel: '관찰 불충분',
      reason: '시선 대상이 불확실하여 해석하지 않음',
      basis: 'uncertain',
    };
  }

  // 1) Explicitly permitted (role or material) → normal, regardless of mode.
  if (isPermitted(cand, ctx)) {
    return {
      action: 'suppress_as_normal',
      neutralLabel: '허용 자료 참조',
      reason: `현재 과제에서 허용된 대상(${cand.label ?? cand.semanticRole})`,
      basis: 'permitted_material',
    };
  }

  // 2) Own exam sheet / paper zone → normal.
  if (cand.targetType === 'paper_zone' || cand.semanticRole === 'own_exam_sheet') {
    return {
      action: 'suppress_as_normal',
      neutralLabel: '본인 시험지 응시',
      reason: '시선이 본인 시험지 평면 안에 있음',
      basis: 'own_sheet',
    };
  }

  // 3) Writing tool → normal.
  if (cand.semanticRole === 'writing_tool') {
    return {
      action: 'suppress_as_normal',
      neutralLabel: '필기도구 사용',
      reason: '필기도구 방향 응시',
      basis: 'writing_tool',
    };
  }

  // 4) Reference material (book/notebook) → context-dependent (the core lever).
  if (cand.semanticRole === 'permitted_reference') {
    if (OPEN_CONTEXTS.has(ctx.examMode)) {
      return {
        action: 'suppress_as_normal',
        neutralLabel: '자료 참조',
        reason: `${ctx.examMode} 맥락에서 자료 참조는 정상`,
        basis: 'permitted_material',
      };
    }
    return {
      action: 'observe',
      neutralLabel: '물리 자료 응시 (검토 참고)',
      reason: `${ctx.examMode} 맥락에서 시험지 외 자료 응시 관찰`,
      basis: 'default',
    };
  }

  // 5) Device candidate (phone/tablet) → observe (never auto-verdict).
  if (cand.semanticRole === 'prohibited_device_candidate') {
    return {
      action: 'observe',
      neutralLabel: '휴대기기 방향 응시 (관찰)',
      reason: '휴대기기 후보 방향 응시 — 단정 아님, 사람 검토 대상',
      basis: 'device_candidate',
    };
  }

  // 6) Another person → observe.
  if (cand.targetType === 'person' || cand.semanticRole === 'other_person') {
    return {
      action: 'observe',
      neutralLabel: '타인 방향 응시 (관찰)',
      reason: '다른 인물 방향 응시',
      basis: 'other_person',
    };
  }

  // 7) Default — surface as a neutral observation.
  return {
    action: 'observe',
    neutralLabel: '대상 응시 (관찰)',
    reason: '분류되지 않은 대상 응시',
    basis: 'default',
  };
}

/**
 * Convenience: build a GazeEventCandidate from a gaze-likelihood result + the
 * registry target its top entry points to. Returns null when the top target is
 * the 'unknown' bucket or no longer in the registry.
 */
export function candidateFromLikelihood(
  gaze: GazeLikelihoodResult,
  registry: TargetRegistry,
  durationMs: number,
): GazeEventCandidate {
  const top = gaze.top;
  if (top.type === 'unknown') {
    return {
      targetType: 'unknown',
      semanticRole: 'unknown',
      durationMs,
      targetProb: top.p,
      entropy: gaze.entropy,
    };
  }
  const tgt = registry.targets.find((t) => t.targetId === top.targetId);
  return {
    targetType: top.type,
    semanticRole: tgt?.semanticRole ?? 'unknown',
    label: tgt?.label,
    durationMs,
    targetProb: top.p,
    entropy: gaze.entropy,
  };
}
